<template>
  <div></div>
</template>

<script>
import { getToken, SubscribeVehicleUpdates } from "@/services/API.js"
import { mapState, mapMutations, mapGetters } from "vuex"
import { getCoor, getColor, getStatus, isUnknown } from "@/utils"
import { GetOrgVehicles, GetOrgVehiclePresence } from "@/services/API"
import Sockette from "sockette"
import { Logger } from "aws-amplify"
const logger = new Logger("WEBSOCKET INFO")

export default {
  data () {
    return {
      restartFunc: false,
      restartInterval: false,
      presenceUpdateInProgress: false,
      orgBeingUpdated: false
    }
  },

  computed: {
    ...mapState([
      "users", 
      "org", 
    ]),

    ...mapState("presence", [
       "vehicles", 
       "selectedOrg"
    ]),

    ...mapGetters("presence", [
      "vehiclesSet",
      "devicesMap",
      "vehicleStatus",
      "mapData"
    ]),

    ...mapState("websockets", [
      "socketEventHandlers",
      "socketSubscriptions",
      "connectionID",
      "currentState",
      "webSocket",
      "activeSubscriptions",
      "manualRefresh"
    ]),

    routeOrgId () {
      try {
        return window.location.href.split('/organisation/')[1].split('/')[0]
      } catch {
        return null
      }
    },
  },

  watch: {
    org (val) {
      this.refreshOrg(val)
    },

    socketSubscriptions(val) {
      // if we haven't had a connection yet, ignore any values
      if(!this.connectionID) {
        return
      }

      const newSubs = val.filter(i => !this.activeSubscriptions.includes(i.name))

      newSubs.forEach(sub => {
        logger.debug(`new sub found for ${sub.name}`, sub)
        this.activeSubscriptions.push(sub.name)
        sub.subscribe(this.connectionID)
      })
    },

    async selectedOrg(val) {
      logger.debug("org changed!", val)

      // if the new value is false, we do nothing
      if(!val) {
        return
      }

      this.refreshOrg(val)
    },

    async manualRefresh() {
      this.refreshOrg(this.selectedOrg)
    }
  },

  mounted () {
    logger.debug("socket component mounted")
    this.addDefaultSubscriptions()
    this.addDefaulthandlers()
    this.initWS()
    this.setOrgToWatch(this.routeOrgId)

    this.restartInterval = setInterval(() => {
      logger.debug("checking for restart function")
      if(this.restartFunc) {
        logger.debug("restart found!")
        this.restartFunc()
        this.restartFunc = false
      }
    }, 30000)
  },

  destroyed () {
    this.setSocket(null)
    this.setSocketID(false)
    clearInterval(this.restartInterval)
  },

  methods: {
    ...mapMutations("websockets", [
      "addSocketEventHandler", 
      "setSocketID", 
      "registerSocketSubscription", 
      "removeOldSubscriptions", 
      "setState", 
      "addActiveSubscription", 
      "resetActiveSubscriptions",
      "setSocket"
    ]),

    ...mapMutations("presence", [
      "setVehicles",
      "updateDevicePresence",
      "setOrgToWatch"
    ]),

    // initialise our web socket
    async initWS() {
      // we do not try to init a WS if our socket already exists
      if(this.webSocket) {
        return
      }

      logger.debug("init web socket")
      try {
        const token = await getToken()
        this.setSocketID(null)
        this.setSocket(new Sockette(`${process.env.VUE_APP_PVWSS}?Auth=${token}`, {
          timeout: 10000,
          maxAttempts: 30,
          onopen: e => this.handleOpen(e),
          onmessage: e => this.handleMessage(e),
          onreconnect: e => this.handleReconnect(e),
          onmaximum: e => this.handleMaxExceeded(e),
          onclose: e => this.handleClose(e),
          onerror: e => this.handleClose(e, "error"),
        }))
      } catch {
        logger.debug("could not initialise socket")
      }
    },

    // handler when websocket is opened
    async handleOpen(e) {
      logger.debug("websocket opened", e)

      if(this.webSocket) {
        try {
          this.webSocket.json({
            action: "getConnectionId"
          })
        } catch (error) {
          logger.debug("web socket could not be opened", error)
          this.restartFunc = () => {
            this.initWS()
          }
        } 
      }
    },

    async handleReconnect (e) {
      this.setSocketID(false)
      logger.debug("websocket reconnect", e)
    },

    async handleClose (e, eventname = 'closed') {
      logger.debug(`websocket ${eventname}`, e)
      logger.debug(`websocket closed. restart function added`, e)
      this.setSocket(null)
      this.restartFunc = () => {
        this.initWS()
      }
    },

    handleMaxExceeded (e) {
      logger.debug("maximum connection attempts exceeded", e)
      logger.debug("a websocket connection could not be established. restart function added")
      this.setSocket(null)
      this.restartFunc = () => {
        this.initWS()
      }
    },

    handleMessage(e) {
      logger.debug("message recieved", e)

      let data = false
      try {
        data = JSON.parse(e.data)
      } catch {
        logger.debug("message could not be parsed", e)
      }
        
      if (!this.connectionID && !data) {
        // update the connection id in our store
        this.setSocketID(e.data)
        logger.debug("connection id", e.data)
        this.renewSubscriptions(e.data) 
        return false
      }

      // this should not happen, but in the event it does!
      if(!this.connectionID && data) {
        logger.debug("recieved a message but websocket id was not set", data, e)
      }

      const property = data.action ? data.action 
        : data.subscription_type ? data.subscription_type
        : false

      logger.debug(`${property} message recieved`, property, e)
      
      // if our websocket has a function defined for this action we execute it here.
      // our websocket functions are stored in an object in Vuex store. 
      if(property && typeof this.socketEventHandlers[property] == 'function') {
         this.socketEventHandlers[property](data)
      } else {
        logger.debug("missing websocket handler", `the action ${property} has no event handler`)
      }

      return false
    },

    // renews any subscriptions we have when the websocket is reconnected
    renewSubscriptions (newSocketID) {
      logger.debug("renewing websocket subscriptions", this.socketSubscriptions)
      // clear our subscription list
      this.resetActiveSubscriptions()
      // remove subscriptions that no longer need to be refreshed
      this.removeOldSubscriptions()

      // run each subscription
      this.socketSubscriptions.forEach(sub => {
        logger.debug(`renewing subscription for ${sub.name}`, sub)
        this.addActiveSubscription(sub.name)
        sub.subscribe(newSocketID)
      })
    },

    // adds any default handlers we have for websocket messages
    addDefaulthandlers () {
      this.addSocketEventHandler({ presenceUpdates: this.handlePresenceUpdates })
    },

    // adds default subscriptions we want to have persist for the life of the site
    addDefaultSubscriptions () {
      // add any default subscriptions here!
    },

    handlePresenceUpdates(response) {    
      try {
        const item = response.value
        if(this.devicesMap) {
          const { lat, long } = item.last_location.coords
          const device = {
            coor: { lat: getCoor(lat), lng: getCoor(long) },
            id: item.logical_id,
            vehicle_id: item.vehicle_id,
            fillColor: getColor(item.status),
            name: this.vehiclesSet[item.vehicle_id].vehicle_name,
            mileage: item.mileage,
            direction: isUnknown(item.last_location.course) ? 0 : parseInt(item.last_location.course),
            type: this.vehiclesSet[item.vehicle_id].vehicle_type.toLowerCase(),
            status: getStatus(item.status),
            statusText: item.status,
            speed: isUnknown(item.current_speed) ? item.current_speed : Number(item.current_speed),
            data: item,
            channels: ["CH1", "CH2", "CH3", "CH4"]
          }

          this.updateDevicePresence(device)
        }
      } catch (exception) {
        logger.debug("pres update failed", exception)
      }
    },

    refreshOrg(val) {
      logger.debug("org refreshed!", val)

      if(!val) {
        return
      }

      if(this.presenceUpdateInProgress && this.orgBeingUpdated == val) {
        logger.debug("we were already refreshing this org!")
        return
      }

      this.presenceUpdateInProgress = true
      this.orgBeingUpdated = val

      // first we get the vehicles for our org
      GetOrgVehicles(val, true).then(async res => {
        logger.debug("compare orgs!", val, this.selectedOrg)
        // We need to compare the selected org vs the org currently being requested
        // due to a lag that may occurr that will cause vehicles outside the selected org to be displayed
        if(val != this.selectedOrg) return
        // if we have a set of vehicles, we continue to setup presence
        if(Array.isArray(res) && res.length) {
          this.setVehicles(res)

          GetOrgVehiclePresence(val).then(pres => {
            logger.debug("pres api response", pres)

            pres.forEach(item => {
              try {  
                const { lat, long } = item.last_location.coords
                const device = {
                  coor: { lat: getCoor(lat), lng: getCoor(long) },
                  id: item.device_id,
                  vehicle_id: item.vehicle_id,
                  fillColor: getColor(item.status),
                  name: this.vehiclesSet[item.vehicle_id].vehicle_name,
                  direction: isUnknown(item.last_location.course) ? 0 : parseInt(item.last_location.course),
                  type: this.vehiclesSet[item.vehicle_id].vehicle_type.toLowerCase(),
                  mileage: item.mileage,
                  status: getStatus(item.status),
                  statusText: item.status,
                  speed: isUnknown(item.current_speed) ? item.current_speed : Number(item.current_speed),
                  data: item,
                  channels: ["CH1", "CH2", "CH3", "CH4"]
                }

                logger.info('device', device)
                this.updateDevicePresence(device)
              } catch (err) {
                logger.info('missing vehicle : ', item, err)
              }  
            }) 
            this.presenceUpdateInProgress = false 
          // error getting pres
          }).catch(err => {
            this.presenceUpdateInProgress = false 
            logger.info('no presence!', err)
          })

          const currentOrg = val
          // finally, we set up our web socket subscription for this org!
          this.registerSocketSubscription({
            name: 'presenceUpdates',
            subscribe: (sockId) => {
              SubscribeVehicleUpdates(currentOrg, {
                connectionId: sockId,
                subscriptionType: "presenceUpdates",
                context: "vehicle"
              })
            },
            refresh: () => {
              logger.debug(currentOrg, this.org.id)
              return currentOrg == this.org.id
            }
          })
        }
      }).catch(err => {
        this.presenceUpdateInProgress = false 
        logger.debug("error getting vehicles for org", err)
      })
    }
  }
}
</script>